import { IoMdClose } from 'react-icons/io';
import { FiEdit2, FiCheck } from 'react-icons/fi';
import { MdOutlineCheckCircle, MdOutlineCancel, MdOutlineArchive, MdOutlinePending, MdOutlineRadioButtonUnchecked } from 'react-icons/md';
import PageForm from '../../templates/PageForm';
import ATable from '../../organisms/ATable';
import Tab, { TabProps } from '../../molecules/Tab';
import SelectTextField from '../../molecules/SelectTextField';
import TextEdit from '../../molecules/TextEdit';
import CopyButton from '../../atoms/CopyButton';
import {
    COLLATERAL_BREADCRUMBS,
    formatCurrency,
    formatDate,
    formatCpfCnpj,
    getIntensityColor,
} from './collateralUtils';
import useGuaranteePageView from './useGuaranteePageView';
import ProcessCard from './ProcessCard';
import GuaranteeStatus from './GuaranteeStatus';
import ValidationStatus from './ValidationStatus';

// --- Skeleton ---
const Skeleton = ({ className = '' }: { className?: string }) => (
    <div className={`animate-pulse bg-gray-200 dark:bg-slate-700 rounded ${className}`} />
);

const CardSkeleton = () => (
    <div className="bg-gray-50 dark:bg-slate-900 rounded-lg p-4 border border-gray-200 dark:border-slate-700">
        <Skeleton className="h-4 w-24 mb-4" />
        <div className="space-y-3">
            {[...Array(6)].map((_, i) => <Skeleton key={i} className="h-3 w-full" />)}
        </div>
    </div>
);

const PageSkeleton = () => (
    <div className="p-6 space-y-6">
        <Skeleton className="h-6 w-96 mb-2" />
        <div className="grid grid-cols-3 gap-4">
            <CardSkeleton />
            <CardSkeleton />
            <CardSkeleton />
        </div>
        <Skeleton className="h-8 w-full" />
        <Skeleton className="h-64 w-full" />
    </div>
);

// --- Field display helpers ---
const Field = ({ label, value, color }: { label: string; value: React.ReactNode; color?: string }) => (
    <div className="flex justify-between items-center py-0.5">
        <span className="text-gray-500 dark:text-gray-400 text-xs">{label}</span>
        {color ? (
            <span className={`px-2 py-0.5 rounded text-[10px] font-medium ${color}`}>{value}</span>
        ) : (
            <span className="text-xs dark:text-white text-right max-w-[60%] truncate" title={String(value ?? '')}>{value != null && value !== '' ? value : '—'}</span>
        )}
    </div>
);

const SectionTitle = ({ children, color }: { children: string; color?: string }) => (
    <h3 className={`text-[10px] font-semibold uppercase mb-2 tracking-wide ${color || 'text-gray-500 dark:text-gray-400'}`}>{children}</h3>
);

// --- Status translation ---
const STATUS_LABELS: Record<string, string> = {
    NOT_STARTED: 'Não iniciado', IN_PROGRESS: 'Em andamento', CHECKED: 'Conferido',
    DONE: 'Concluído', SETTLED: 'Quitada', PARTIALLY_VALIDATED: 'Parcialmente validada',
    FULLY_VALIDATED: '100% Validada', STARTED: 'Iniciado', IN_VALIDATION: 'Em validação',
    AWAITING_DOC: 'Aguardando doc', HAS_PENDENCY: 'Tem pendência', 'N/A': 'N/A',
    BAIXA: 'BAIXA', MEDIA: 'MÉDIA', ALTA: 'ALTA', CAOTICA: 'CAÓTICA',
};
const t = (v: string) => STATUS_LABELS[v] || v;

// --- Process validation icons ---
const ProcessIcon = ({ received, status }: { received?: boolean; status?: string }) => {
    if (!received) return <MdOutlineRadioButtonUnchecked className="text-gray-300" size={14} title="Não recebido" />;
    switch (status) {
        case 'VALIDATED': return <MdOutlineCheckCircle className="text-green-500" size={14} title="Validado" />;
        case 'REJECTED': return <MdOutlineCancel className="text-red-500" size={14} title="Rejeitado" />;
        case 'ARCHIVED': return <MdOutlineArchive className="text-blue-500" size={14} title="Arquivado" />;
        default: return <MdOutlinePending className="text-amber-500" size={14} title="Pendente" />;
    }
};

// --- Constants ---
const PROCESS_LABELS: Record<string, string> = {
    XML: 'XML', DRAWEE: 'Sacado', INVOICE_PDF: 'NF PDF',
    PURCHASE_CONTRACT: 'Contrato C/V', RECEIPT_STUB: 'Canhoto',
    SEFAZ: 'SEFAZ', ACCEPTED_BILL: 'Duplicata',
};
const ALL_PROCESSES = ['XML', 'DRAWEE', 'INVOICE_PDF', 'PURCHASE_CONTRACT', 'RECEIPT_STUB', 'SEFAZ', 'ACCEPTED_BILL'];
const TAB_ITEMS: TabProps[] = [
    { id: 'general', title: 'Geral', index: 0 },
    { id: 'metrics', title: 'Métricas e checagem', index: 1 },
    { id: 'receivables', title: 'Recebíveis', index: 2 },
];

// --- Main Component ---
const GuaranteePageView = () => {
    const {
        id, navigate, isLoading, data, tabIndex, setTabIndex,
        titles, enabledProcesses, summary,
        selectedIds, setSelectedIds, editingIds,
        showValidationPanel, setShowValidationPanel,
        batchProcess, setBatchProcess, batchStatus, setBatchStatus,
        batchObservation, setBatchObservation, isSaving,
        sortField, setSortField, sortOrder, setSortOrder,
        openBatchPanel, openEditPanel, toggleSelect, handleSaveValidation,
    } = useGuaranteePageView();

    if (isLoading) return <PageSkeleton />;
    if (!data) return <div className="p-8 text-red-600">Garantia não encontrada</div>;

    return (
        <PageForm
            pageTitle={`${(data.operationCode || '').substring(0, 10)} — ${(data.assignor || '').substring(0, 40)}${(data.assignor || '').length > 40 ? '...' : ''}`}
            apiVersion={data.apiVersion ? `${data.apiName} - ${data.apiVersion} (${(data.environment || '').toUpperCase()})` : ''}
            isLoading={false}
            breadcrumb={[
                COLLATERAL_BREADCRUMBS.guarantees,
                { label: (data.operationCode || '').substring(0, 10), url: '#' },
            ]}
            controlSlot={
                <div className="flex justify-end space-x-2">
                    {tabIndex === 2 && selectedIds.size > 0 && (
                        <button onClick={() => openBatchPanel()}
                            className="bg-green-600 hover:bg-green-700 text-white px-3 py-2 rounded text-sm flex items-center space-x-2">
                            <FiCheck size={14} /> <span>Validar ({selectedIds.size})</span>
                        </button>
                    )}
                    {tabIndex === 0 && (
                        <button onClick={() => navigate(`/collateral/guarantees/${id}/view/edit`)}
                            className="bg-blue-500 hover:bg-blue-600 text-white px-3 py-2 rounded text-sm flex items-center space-x-2">
                            <FiEdit2 size={14} /> <span>Editar</span>
                        </button>
                    )}
                </div>
            }
        >
            <div className="mt-4">
                <Tab tabItems={TAB_ITEMS} tabIndex={tabIndex} setTabIndex={setTabIndex} />

                {tabIndex === 0 && <GeneralTab data={data} summary={summary} />}
                {tabIndex === 1 && <MetricsTab data={data} enabledProcesses={enabledProcesses} />}
                {tabIndex === 2 && (
                    <ReceivablesTab
                        titles={titles} enabledProcesses={enabledProcesses}
                        selectedIds={selectedIds} setSelectedIds={setSelectedIds}
                        sortField={sortField} setSortField={setSortField}
                        sortOrder={sortOrder} setSortOrder={setSortOrder}
                        toggleSelect={toggleSelect} openEditPanel={openEditPanel}
                    />
                )}
            </div>

            {showValidationPanel && (
                <ValidationPanel
                    titles={titles} enabledProcesses={enabledProcesses}
                    editingIds={editingIds} selectedIds={selectedIds}
                    batchProcess={batchProcess} setBatchProcess={setBatchProcess}
                    batchStatus={batchStatus} setBatchStatus={setBatchStatus}
                    batchObservation={batchObservation} setBatchObservation={setBatchObservation}
                    isSaving={isSaving}
                    onClose={() => setShowValidationPanel(false)}
                    onSave={handleSaveValidation}
                />
            )}
        </PageForm>
    );
};

// --- Tab: Geral ---
const GeneralTab = ({ data, summary }: { data: any; summary: any }) => (
    <div className="mt-4 space-y-4">
        <div className="grid grid-cols-[2fr_1fr] gap-4">
            <div className="bg-green-50 dark:bg-green-900/10 rounded-lg p-4 border border-green-200 dark:border-green-800">
                <SectionTitle color="text-green-700 dark:text-green-400">Configuração</SectionTitle>
                <div className="grid grid-cols-2 gap-x-6 divide-x divide-green-200 dark:divide-green-800">
                    <div className="space-y-0.5">
                        <Field label="Tipo - Obrigação" value={`${data.operationType} - ${({ 'WITH_RECOURSE': 'Com regresso', 'WITHOUT_RECOURSE': 'Sem regresso', 'NO_OBLIGATION': 'Sem garantia' } as Record<string, string>)[data.obligation] || data.obligation || '—'}`} />
                        <Field label="Cedente" value={data.assignor} />
                        <Field label="CNPJ Cedente" value={formatCpfCnpj(data.assignorTaxId)} />
                        <Field label="Intensidade" value={t(data.analysisIntensity)} color={data.analysisIntensity ? getIntensityColor(data.analysisIntensity) : undefined} />
                        <Field label="Critério análise" value={data.analysisCriteria || '—'} />
                        {data.sampledTitles && <Field label="Títulos amostragem" value={data.sampledTitles} />}
                        <Field label="Data emissão" value={formatDate(data.issueDate)} />
                    </div>
                    <div className="space-y-0.5 pl-6">
                        <Field label="Resp. análise" value={data.analystName} />
                        <Field label="Trader" value={data.trader} />
                        <Field label="Pendência" value={data.pendency} />
                        <Field label="Observação" value={data.observation} />
                    </div>
                </div>
            </div>
            <div className="flex flex-col gap-4">
                <div className="bg-blue-50 dark:bg-blue-900/10 rounded-lg p-4 border border-blue-200 dark:border-blue-800">
                    <SectionTitle color="text-blue-700 dark:text-blue-400">Resumo</SectionTitle>
                    <div className="space-y-1">
                        <Field label="Nº de títulos" value={data.totalTitles} />
                        <Field label="Moeda" value={data.currency || 'BRL'} />
                        <Field label="Total de títulos" value={formatCurrency(summary.totalAmount)} />
                        <Field label="Total validado" value={formatCurrency(summary.validatedAmount)} />
                    </div>
                </div>
                <div className="bg-amber-50 dark:bg-amber-900/10 rounded-lg p-4 border border-amber-200 dark:border-amber-800">
                    <SectionTitle color="text-amber-700 dark:text-amber-400">Status</SectionTitle>
                    <div className="space-y-1">
                        <div className="flex justify-between items-center py-0.5">
                            <span className="text-gray-500 dark:text-gray-400 text-xs">Status garantia</span>
                            <GuaranteeStatus status={data.guaranteeStatus} />
                        </div>
                        <div className="flex justify-between items-center py-0.5">
                            <span className="text-gray-500 dark:text-gray-400 text-xs">Status validação</span>
                            <ValidationStatus status={data.validationStatus} />
                        </div>
                    </div>
                </div>
            </div>
        </div>
        <div className="grid grid-cols-3 gap-4">
            <div className="p-3 bg-purple-50 dark:bg-purple-900/20 border border-purple-200 dark:border-purple-800 rounded">
                <Field label="Lastro salvo em garantias?" value={data.collateralSaved ? 'SIM' : 'NÃO'} />
            </div>
            <div className={`p-3 border rounded ${data.debtorNotificationRequested ? 'bg-pink-50 dark:bg-pink-900/20 border-pink-200 dark:border-pink-800' : 'bg-gray-100 dark:bg-slate-900 border-gray-200 dark:border-slate-700 opacity-60'}`}>
                <Field label="Notificação de sacados solicitada" value={data.debtorNotificationRequested ? 'SIM' : 'NÃO'} />
                {data.debtorNotificationRequested && (
                    <Field label="Notificação realizada" value={data.debtorNotificationDone ? 'SIM' : 'NÃO'} />
                )}
            </div>
            <div className="p-3 bg-pink-50 dark:bg-pink-900/20 border border-pink-200 dark:border-pink-800 rounded space-y-2">
                <Field label="Registro em cartório" value={data.courtRegistration ? 'SIM' : 'NÃO'} />
                <Field label="Qual contrato registrar?" value={data.contractToRegister} />
            </div>
        </div>
    </div>
);

export default GuaranteePageView;

// --- Tab: Métricas e checagem ---
const MetricsTab = ({ data, enabledProcesses }: { data: any; enabledProcesses: string[] }) => (
    <div className="mt-4 space-y-4">
        <div className="grid grid-cols-4 gap-3">
            {ALL_PROCESSES.slice(0, 4).map(p => {
                const d = data.processValidations?.find((pv: any) => pv.processType === p) || {};
                return <ProcessCard key={p} label={PROCESS_LABELS[p]} isEnabled={enabledProcesses.includes(p)} status={d.status} receivedCount={d.received_count ?? d.receivedCount} pendingCount={d.pending_count ?? d.pendingCount} validatedCount={d.validated_count ?? d.validatedCount} />;
            })}
        </div>
        <div className="grid grid-cols-3 gap-3">
            {ALL_PROCESSES.slice(4).map(p => {
                const d = data.processValidations?.find((pv: any) => pv.processType === p) || {};
                return <ProcessCard key={p} label={PROCESS_LABELS[p]} isEnabled={enabledProcesses.includes(p)} status={d.status} receivedCount={d.received_count ?? d.receivedCount} pendingCount={d.pending_count ?? d.pendingCount} validatedCount={d.validated_count ?? d.validatedCount} />;
            })}
        </div>

        <div className="bg-white dark:bg-slate-800 border border-gray-200 dark:border-slate-700 rounded-lg p-4">
            <span className="text-[10px] font-semibold text-gray-600 dark:text-gray-300 uppercase">Progresso do Lote</span>
            <div className="mt-3 space-y-3">
                {ALL_PROCESSES.filter(p => enabledProcesses.includes(p)).map(p => {
                    const d = data.processValidations?.find((pv: any) => pv.processType === p) || {};
                    const total = d.total_count ?? d.totalCount ?? data.totalTitles ?? 0;
                    const validated = d.validated_count ?? d.validatedCount ?? 0;
                    const pending = d.pending_count ?? d.pendingCount ?? 0;
                    const received = d.received_count ?? d.receivedCount ?? 0;
                    const rejected = received - validated - pending;
                    const pctValidated = total > 0 ? (validated / total) * 100 : 0;
                    const pctPending = total > 0 ? (pending / total) * 100 : 0;
                    const pctRejected = total > 0 ? (Math.max(0, rejected) / total) * 100 : 0;
                    return (
                        <div key={p}>
                            <div className="flex justify-between text-xs mb-1">
                                <span className="text-gray-600 dark:text-gray-300">{PROCESS_LABELS[p]}</span>
                                <span className="text-gray-500">{validated}/{total}</span>
                            </div>
                            <div className="w-full bg-gray-200 dark:bg-slate-700 rounded-full h-2 flex overflow-hidden">
                                {pctValidated > 0 && <div className="bg-green-500 h-2" style={{ width: `${pctValidated}%` }} />}
                                {pctPending > 0 && <div className="bg-amber-400 h-2" style={{ width: `${pctPending}%` }} />}
                                {pctRejected > 0 && <div className="bg-red-500 h-2" style={{ width: `${pctRejected}%` }} />}
                            </div>
                        </div>
                    );
                })}
            </div>
            <div className="border-t border-gray-200 dark:border-slate-700 mt-4 pt-3 space-y-2">
                <div className="flex justify-between text-xs"><span className="text-gray-600 dark:text-gray-300 font-medium">Total de títulos</span><span className="dark:text-white font-medium">{data.totalTitles}</span></div>
                <div className="flex justify-between text-xs"><span className="text-gray-600 dark:text-gray-300 font-medium">Valor total</span><span className="dark:text-white font-medium">{formatCurrency(data.totalAmount || 0)}</span></div>
                <div className="flex justify-between text-xs"><span className="text-gray-600 dark:text-gray-300 font-medium">% validado</span><span className="dark:text-white font-medium">{data.validatedPercentage || 0}%</span></div>
            </div>
            <div className="border-t border-gray-200 dark:border-slate-700 mt-3 pt-3">
                <div className="flex justify-between items-center text-xs">
                    <span className="text-gray-600 dark:text-gray-300 font-medium">Intensidade</span>
                    {data.analysisIntensity && <span className={`px-2 py-0.5 rounded-full text-[10px] font-medium ${getIntensityColor(data.analysisIntensity)}`}>{t(data.analysisIntensity)}</span>}
                </div>
                <div className="flex justify-between text-xs mt-1"><span className="text-gray-500">Critério</span><span className="dark:text-white">{data.analysisCriteria || '—'}</span></div>
                {data.sampledTitles && <div className="flex justify-between text-xs mt-1"><span className="text-gray-500">Títulos amostragem</span><span className="dark:text-white">{data.sampledTitles}</span></div>}
            </div>
        </div>
    </div>
);

// --- Tab: Recebíveis ---
const ReceivablesTab = ({ titles, enabledProcesses, selectedIds, setSelectedIds, sortField, setSortField, sortOrder, setSortOrder, toggleSelect, openEditPanel }: {
    titles: any[]; enabledProcesses: string[];
    selectedIds: Set<string>; setSelectedIds: (s: Set<string>) => void;
    sortField: string; setSortField: (f: string) => void;
    sortOrder: 'ASC' | 'DESC'; setSortOrder: (o: 'ASC' | 'DESC') => void;
    toggleSelect: (id: string) => void; openEditPanel: (id: string) => void;
}) => (
    <div className="mt-3 overflow-hidden" style={{ height: 'calc(100vh - 280px)' }}>
        <div className="h-full overflow-auto border border-gray-200 dark:border-slate-700">
            <ATable
                headers={[
                    { id: 'titleNumber', title: 'Título', field: 'titleNumber', canSort: true, width: '60px' },
                    { id: 'debtorName', title: 'Sacado', field: 'debtorName', canSort: true },
                    { id: 'city', title: 'Município - UF', field: 'city', canSort: true, width: '140px' },
                    { id: 'receivableNumber', title: 'nDup', field: 'receivableNumber', canSort: true, width: '45px' },
                    { id: 'amount', title: 'vDup', field: 'amount', canSort: true, width: '90px', cssClass: 'text-right' },
                    { id: 'dueDate', title: 'Venc.', field: 'dueDate', canSort: true, width: '80px' },
                    ...ALL_PROCESSES.map(p => ({
                        id: `proc_${p}`, title: PROCESS_LABELS[p] || p, field: `proc_${p}`,
                        canSort: false, width: '55px',
                        cssClass: `text-center ${!enabledProcesses.includes(p) ? 'text-gray-300 dark:text-gray-600' : ''}`,
                    })),
                    { id: 'docStatus', title: 'Status', field: 'docStatus', canSort: true, width: '60px', cssClass: 'text-center' },
                    { id: 'actions', title: '', field: 'actions', canSort: false, width: '15px', cssClass: 'text-center' },
                ]}
                fieldSorted={sortField}
                orderSorted={sortOrder}
                onSort={(field, order) => { setSortField(field); setSortOrder(order); }}
                canSelectAll
                onSelect={(checked: boolean) => {
                    setSelectedIds(checked ? new Set(titles.filter((t: any) => t.canValidate).map((t: any) => t.id)) : new Set());
                }}
            >
                {titles.map((row: any, index: number) => {
                    const processItems: Record<string, any> = {};
                    (row.processItems || []).forEach((pi: any) => { processItems[pi.processType] = pi; });
                    return (
                        <tr key={row.id} className={`group hover:bg-blue-50 dark:hover:bg-slate-700 ${index % 2 === 0 ? 'bg-white dark:bg-slate-800' : 'bg-gray-50 dark:bg-slate-900'}`}>
                            <td className="py-2 px-1 border-b border-gray-200 dark:border-slate-700 text-center">
                                {row.canValidate && (
                                    <input type="checkbox" checked={selectedIds.has(row.id)} onChange={() => toggleSelect(row.id)}
                                        className="w-4 h-4 text-blue-600 bg-gray-100 border-gray-300 rounded-sm focus:ring-blue-500" />
                                )}
                            </td>
                            <td className="py-2 px-3 border-b border-gray-200 dark:border-slate-700 text-xs dark:text-white">{row.titleNumber}</td>
                            <td className="py-1 px-3 border-b border-gray-200 dark:border-slate-700">
                                <div className="text-xs dark:text-white truncate" title={row.debtorName}>{row.debtorName}</div>
                                <div className="text-[11px] text-gray-400 flex items-center gap-1">
                                    <span>{formatCpfCnpj(row.debtorDocument)}</span>
                                    <CopyButton data={row.debtorDocument} label="Copiar documento" className="p-0 hover:bg-transparent" />
                                    <span className="mx-2">·</span>
                                    <span title={row.nfeKey}>{row.nfeKey}</span>
                                    <CopyButton data={row.nfeKey} label="Copiar NFe" className="p-0 hover:bg-transparent" />
                                </div>
                            </td>
                            <td className="py-2 px-3 border-b border-gray-200 dark:border-slate-700 text-xs dark:text-white">{row.city}{row.state ? ` - ${row.state}` : ''}</td>
                            <td className="py-2 px-3 border-b border-gray-200 dark:border-slate-700 text-xs dark:text-white">{row.receivableNumber || '—'}</td>
                            <td className="py-2 px-3 border-b border-gray-200 dark:border-slate-700 text-xs dark:text-white text-right">{formatCurrency(row.amount)}</td>
                            <td className="py-2 px-3 border-b border-gray-200 dark:border-slate-700 text-xs dark:text-white">{formatDate(row.dueDate)}</td>
                            {ALL_PROCESSES.map(p => {
                                const pi = processItems[p];
                                const isEnabled = enabledProcesses.includes(p);
                                return (
                                    <td key={p} className="py-2 px-1 border-b border-gray-200 dark:border-slate-700 text-center">
                                        {isEnabled ? (
                                            <div className="flex flex-col items-center gap-0.5">
                                                {pi?.received
                                                    ? <MdOutlineCheckCircle className="text-green-400" size={10} title="Recebido" />
                                                    : <MdOutlineRadioButtonUnchecked className="text-gray-300" size={10} title="Não recebido" />
                                                }
                                                <ProcessIcon received={pi?.received} status={pi?.status} />
                                            </div>
                                        ) : (
                                            <span className="text-gray-200 dark:text-gray-700">—</span>
                                        )}
                                    </td>
                                );
                            })}
                            <td className="py-2 px-3 border-b border-gray-200 dark:border-slate-700 text-xs text-center">
                                <span className={!row.canValidate ? 'text-green-600 font-medium' : 'text-gray-500'}>{row.docStatus || '—'}</span>
                            </td>
                            <td>
                                {!row.canValidate && (
                                    <button onClick={() => openEditPanel(row.id)}
                                        className="opacity-0 group-hover:opacity-100 transition-opacity text-gray-400 hover:text-blue-600">
                                        <FiEdit2 size={12} />
                                    </button>
                                )}
                            </td>
                        </tr>
                    );
                })}
            </ATable>
        </div>
    </div>
);

// --- Validation Side Panel ---
const ValidationPanel = ({ titles, enabledProcesses, editingIds, selectedIds, batchProcess, setBatchProcess, batchStatus, setBatchStatus, batchObservation, setBatchObservation, isSaving, onClose, onSave }: {
    titles: any[]; enabledProcesses: string[];
    editingIds: string[]; selectedIds: Set<string>;
    batchProcess: Record<string, { received: string; status: string; observation: string }>;
    setBatchProcess: (fn: any) => void;
    batchStatus: string; setBatchStatus: (s: string) => void;
    batchObservation: string; setBatchObservation: (s: string) => void;
    isSaving: boolean; onClose: () => void; onSave: () => void;
}) => {
    const selectedTitles = titles.filter((t: any) => editingIds.length > 0 ? editingIds.includes(t.id) : selectedIds.has(t.id));

    return (
        <div className="fixed inset-0 z-50 flex">
            <div className="flex-1 bg-black/30" onClick={onClose} />
            <button onClick={onClose}
                className="fixed top-[15px] right-[585px] w-8 h-8 rounded-full shadow-lg z-[9999] flex items-center justify-center border border-gray-300 dark:border-slate-600 bg-gray-500 hover:bg-gray-600 text-white">
                <IoMdClose size={16} />
            </button>
            <div className="w-[600px] h-full bg-white dark:bg-slate-800 shadow-xl overflow-y-auto border-l border-gray-200 dark:border-slate-700">
                <div className="sticky top-0 flex items-center justify-between p-4 border-b border-gray-200 dark:border-slate-700 bg-white dark:bg-slate-800 z-10">
                    <span className="text-sm font-semibold dark:text-white">{editingIds.length > 0 ? 'Editar validação' : `Editar validação (${selectedIds.size})`}</span>
                    <button onClick={onSave} disabled={isSaving}
                        className="px-3 py-1.5 bg-green-600 text-white rounded hover:bg-green-700 text-xs flex items-center space-x-1 disabled:opacity-50">
                        <FiCheck size={12} /> <span>{isSaving ? 'Salvando...' : 'Salvar'}</span>
                    </button>
                </div>
                <div className="p-4 space-y-4">
                    <div className="bg-gray-50 dark:bg-slate-900 rounded p-3 border border-gray-100 dark:border-slate-700">
                        <div className="text-xs font-medium text-gray-600 dark:text-gray-300 mb-2">Recebíveis selecionados</div>
                        <div className="max-h-32 overflow-y-auto space-y-1">
                            {selectedTitles.map((t: any) => (
                                <div key={t.id} className="flex justify-between text-xs">
                                    <span className="dark:text-white">{t.titleNumber} - {t.debtorName}</span>
                                    <span className="text-gray-500">{formatCurrency(t.amount)}</span>
                                </div>
                            ))}
                        </div>
                    </div>

                    <div className="text-xs font-semibold text-gray-500 uppercase mt-4 mb-2">Validações por processo</div>
                    <div className="grid grid-cols-2 gap-3">
                        {ALL_PROCESSES.filter(p => enabledProcesses.includes(p) && p !== 'DRAWEE').map(p => {
                            const received = batchProcess[p]?.received;
                            const status = batchProcess[p]?.status;
                            const topBorder = status === 'VALIDATED' ? 'border-t-green-500'
                                : status === 'REJECTED' ? 'border-t-red-500'
                                    : status === 'PENDING' ? 'border-t-amber-400'
                                        : received === 'true' ? 'border-t-blue-500'
                                            : 'border-t-gray-300 dark:border-t-slate-600';
                            return (
                                <div key={p} className={`border border-gray-200 dark:border-slate-700 border-t-4 ${topBorder} rounded p-3 space-y-2`}>
                                    <span className="text-[10px] font-semibold text-gray-600 dark:text-gray-300 uppercase">{PROCESS_LABELS[p]}</span>
                                    <SelectTextField label="Recebido" value={received || ''} items={[{ value: 'true', label: 'Sim' }, { value: 'false', label: 'Não' }]} onChange={(e: any) => setBatchProcess((prev: any) => ({ ...prev, [p]: { ...prev[p], received: e.target.value, status: prev[p]?.status || '', observation: prev[p]?.observation || '' } }))} />
                                    <SelectTextField label="Status" value={status || ''} items={[{ value: 'PENDING', label: 'Pendente' }, { value: 'VALIDATED', label: 'Validado' }, { value: 'REJECTED', label: 'Rejeitado' }]} onChange={(e: any) => setBatchProcess((prev: any) => ({ ...prev, [p]: { ...prev[p], received: prev[p]?.received || '', status: e.target.value, observation: prev[p]?.observation || '' } }))} />
                                    <TextEdit label="Observação" value={batchProcess[p]?.observation || ''} onChange={(e: any) => setBatchProcess((prev: any) => ({ ...prev, [p]: { ...prev[p], received: prev[p]?.received || '', status: prev[p]?.status || '', observation: e.target.value } }))} />
                                </div>
                            );
                        })}
                    </div>

                    <div className="text-xs font-semibold text-gray-500 uppercase mt-4 mb-2">Status do Recebível</div>
                    <div className="space-y-3">
                        <SelectTextField label="Status" value={batchStatus} items={[{ value: 'PENDING', label: 'Pendente' }, { value: 'OK', label: 'OK' }, { value: 'NOT_OK', label: 'Não OK' }]} onChange={(e: any) => setBatchStatus(e.target.value)} />
                        <TextEdit label="Observação" value={batchObservation} onChange={(e: any) => setBatchObservation(e.target.value)} />
                    </div>
                </div>
            </div>
        </div>
    );
};
